// "Das könnte Ihnen auch gefallen" grid: every cell shows a random product from the pool and
// switches to another random product every few seconds. All visible cells always show
// different products, and each new product differs from the one it replaces.
// Pauses while off-screen, while a cell is hovered/focused, in hidden tabs and for reduced motion.
class SProductShuffle extends HTMLElement {
  connectedCallback() {
    const source = this.querySelector('script[type="application/json"][data-pool]');
    try {
      this.pool = JSON.parse(source ? source.textContent : '[]');
    } catch (error) {
      this.pool = [];
    }
    this.cells = Array.from(this.querySelectorAll('[data-shuffle-cell]'));
    if (!this.cells.length || this.pool.length < 2) return;

    this.interval = Math.max(Number(this.dataset.interval) || 3000, 1000);
    this.fade = 900;
    this.visible = false;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    // Start from a random, all-distinct selection.
    const start = this.shuffle(this.pool.map((_, index) => index)).slice(0, this.cells.length);
    this.current = this.cells.map((cell, i) => {
      this.paint(cell, this.pool[start[i]]);
      return start[i];
    });

    this.cells.forEach((cell) => {
      cell.addEventListener('pointerenter', () => { cell.dataset.hold = '1'; });
      cell.addEventListener('pointerleave', () => { delete cell.dataset.hold; });
      cell.addEventListener('focusin', () => { cell.dataset.hold = '1'; });
      cell.addEventListener('focusout', () => { delete cell.dataset.hold; });
    });

    if (this.reducedMotion.matches || this.pool.length <= this.cells.length) return;

    this.observer = new IntersectionObserver((entries) => {
      this.visible = entries[0].isIntersecting;
    }, { rootMargin: '100px' });
    this.observer.observe(this);
    this.timer = setInterval(() => this.tick(), this.interval);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
    this.observer?.disconnect();
  }

  shuffle(list) {
    const copy = list.slice();
    for (let i = copy.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  }

  tick() {
    if (!this.visible || document.hidden) return;
    // Products already on screen stay reserved so no two cells ever match.
    const taken = new Set(this.current);
    const free = this.shuffle(this.pool.map((_, index) => index).filter((index) => !taken.has(index)));
    this.cells.forEach((cell, i) => {
      if (cell.dataset.hold || !free.length) return;
      // Only products not on screen this round are eligible, so each new pick differs from
      // every cell, including the one it replaces. Small pools just change fewer cells.
      const next = free.pop();
      this.current[i] = next;
      setTimeout(() => this.swap(cell, this.pool[next]), i * 160);
    });
  }

  // Crossfade: the new image is preloaded, then fades in on top of the old one (no blank frame);
  // title and price dissolve softly at the same time. Ends by committing the new product to the cell.
  swap(cell, product) {
    const base = cell.querySelector('[data-shuffle-image]');
    const frame = base ? base.parentElement : null;
    const texts = cell.querySelectorAll('[data-shuffle-title], [data-shuffle-price]');
    const duration = this.fade;
    const easing = 'cubic-bezier(0.4, 0, 0.2, 1)';

    const run = () => {
      texts.forEach((text) => {
        text.style.transition = `opacity ${duration / 2}ms ${easing}`;
        text.style.opacity = '0';
      });
      setTimeout(() => {
        const title = cell.querySelector('[data-shuffle-title]');
        const price = cell.querySelector('[data-shuffle-price]');
        if (title) title.textContent = product.title;
        if (price) price.textContent = product.price;
        cell.querySelectorAll('[data-shuffle-link]').forEach((link) => {
          link.href = product.url;
        });
        texts.forEach((text) => {
          text.style.opacity = '1';
        });
      }, duration / 2);

      if (!frame || !base || !product.image) {
        setTimeout(() => this.paint(cell, product), duration);
        return;
      }
      if (getComputedStyle(frame).position === 'static') frame.style.position = 'relative';
      const overlay = base.cloneNode();
      overlay.removeAttribute('data-shuffle-image');
      overlay.removeAttribute('loading');
      overlay.src = product.image;
      overlay.alt = '';
      overlay.setAttribute('aria-hidden', 'true');
      Object.assign(overlay.style, {
        position: 'absolute',
        inset: '0',
        opacity: '0',
        transform: 'scale(1.03)',
        transition: `opacity ${duration}ms ${easing}, transform ${duration}ms ${easing}`,
        pointerEvents: 'none',
        visibility: 'visible',
      });
      frame.append(overlay);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          overlay.style.opacity = '1';
          overlay.style.transform = 'scale(1)';
        });
      });
      setTimeout(() => {
        this.paint(cell, product);
        // Remove the overlay only after the base image has the new picture decoded.
        const finish = () => overlay.remove();
        if (base.decode) base.decode().then(finish, finish);
        else finish();
      }, duration + 50);
    };

    if (product.image) {
      const preload = new Image();
      preload.onload = run;
      preload.onerror = run;
      preload.src = product.image;
    } else {
      run();
    }
  }

  paint(cell, product) {
    cell.querySelectorAll('[data-shuffle-link]').forEach((link) => {
      link.href = product.url;
    });
    const img = cell.querySelector('[data-shuffle-image]');
    if (img) {
      if (product.image) {
        img.src = product.image;
        img.alt = product.title;
        img.style.visibility = 'visible';
      } else {
        img.removeAttribute('src');
        img.alt = '';
        img.style.visibility = 'hidden';
      }
    }
    const title = cell.querySelector('[data-shuffle-title]');
    if (title) title.textContent = product.title;
    const price = cell.querySelector('[data-shuffle-price]');
    if (price) price.textContent = product.price;
  }
}

if (!customElements.get('s-product-shuffle')) customElements.define('s-product-shuffle', SProductShuffle);
